import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { isValidPhone } from '../../../../utils/contact';
import { usePatientCard } from '../../../../hooks/usePatientCard';
import type { GetPatientResponse } from '../../../../services/patientCard';
import { editPatient, type EditPatientBody } from '../../../../services/patientEdit';
import { useToast } from '../../../../providers/ToastProvider';
import { DmShellLayout } from '../dm/DmShellLayout';
import { SkeletonCard } from '../dm/DmSkeletons';
import {
    DmField,
    dmInputClasses,
    dmInputErrorClasses,
    DmDateInput,
    DmSelect,
    DmTextarea,
    DmBtnPrimary,
    DmBtnSecondary
} from '../dm/DmFormPrimitives';
import type { ActionRunnerProps } from '../types';
import {
    EDIT_PATIENT_GROUPS,
    fieldsInGroup,
    type EditPatientFieldConfig
} from './editPatientFields';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type FormValues = Record<string, string>;
type FormErrors = Record<string, string>;

const ALL_FIELDS = EDIT_PATIENT_GROUPS.flatMap((group) => fieldsInGroup(group));

/** Every field key mapped to ''. Initial `values`/`original` state, so
 *  `buildDiff`/`validateForm` never call `.trim()` on `undefined` before
 *  the prefill effect or the card query resolves. */
function buildEmptyFormValues(): FormValues {
    return Object.fromEntries(ALL_FIELDS.map((field) => [field.key, '']));
}

function getPrefillValue(card: GetPatientResponse, field: EditPatientFieldConfig): string {
    switch (field.key) {
        case 'firstName':
            return card.firstName ?? '';
        case 'lastName':
            return card.lastName ?? '';
        case 'email':
            return card.email ?? '';
        case 'phone':
            return card.phone ?? '';
        case 'gender':
            return card.gender ?? '';
        case 'dateOfBirth':
            return card.dateOfBirth ?? '';
        case 'version':
            return card.status?.version ?? '';
        case 'caseType':
            return card.treatment?.caseType ?? '';
        case 'subCaseType':
            return card.treatment?.subCaseType ?? '';
        case 'manufacturer':
            return card.treatment?.manufacturer ?? '';
        case 'alignerOption':
            return card.treatment?.alignerOption ?? '';
        case 'refinementsCount':
            return card.treatment?.refinementsCount != null
                ? String(card.treatment.refinementsCount)
                : '';
        case 'treatmentGoals':
            return card.notes?.treatmentGoals ?? '';
        case 'additionalInfo':
            return card.treatment?.additionalInfo ?? '';
        case 'iteroScanId':
            return card.monitoring?.iteroScanId ?? '';
        default:
            return '';
    }
}

function validateForm(values: FormValues, fields: EditPatientFieldConfig[]): FormErrors {
    const errors: FormErrors = {};
    for (const field of fields) {
        const value = values[field.key] ?? '';
        if (field.required && !value.trim()) {
            errors[field.key] = `${field.label} is required.`;
            continue;
        }
        if (field.key === 'email' && value.trim() && !EMAIL_REGEX.test(value.trim())) {
            errors[field.key] = 'Enter a valid email address.';
        }
        if (field.inputType === 'phone' && value.trim() && !isValidPhone(value)) {
            errors[field.key] = 'Please enter a valid US or Canadian phone number.';
        }
    }
    return errors;
}

/** Exact-value inputs (dates, selects) shouldn't be trimmed - every other input is free text. */
const EXACT_VALUE_INPUT_TYPES = new Set<EditPatientFieldConfig['inputType']>(['date', 'select']);

function buildDiff(
    values: FormValues,
    original: FormValues,
    fields: EditPatientFieldConfig[]
): EditPatientBody {
    const diff: Record<string, string> = {};
    for (const field of fields) {
        const exact = EXACT_VALUE_INPUT_TYPES.has(field.inputType);
        const current = exact ? values[field.key] : values[field.key].trim();
        const originalValue = exact ? original[field.key] : original[field.key].trim();
        if (current !== originalValue) {
            diff[field.key] = current;
        }
    }
    return diff as EditPatientBody;
}

/** Prefills `values`/`original` from the patient card exactly once (per mount),
 *  as soon as it loads - split out of `useEditPatientForm` to keep that hook short. */
function usePrefillableFormValues(card: GetPatientResponse | null | undefined) {
    const [values, setValues] = useState<FormValues>(buildEmptyFormValues);
    const [original, setOriginal] = useState<FormValues>(buildEmptyFormValues);
    const prefilled = useRef(false);

    useEffect(() => {
        if (card && !prefilled.current) {
            prefilled.current = true;
            const initial = Object.fromEntries(
                ALL_FIELDS.map((field) => [field.key, getPrefillValue(card, field)])
            );
            setValues(initial);
            setOriginal(initial);
        }
    }, [card]);

    const handleChange = useCallback((key: string, value: string) => {
        setValues((prev) => ({ ...prev, [key]: value }));
    }, []);

    return { values, original, handleChange };
}

/** Encapsulates all form state/mutation plumbing so the component itself only renders.
 *  Outcomes surface as toasts: success navigates back to the previous screen,
 *  failure keeps the form (and its values) on screen for another attempt. */
function useEditPatientForm(patientId: string, onBack: () => void) {
    const { data: card, isLoading: cardLoading, refetch } = usePatientCard(patientId);
    const { values, original, handleChange } = usePrefillableFormValues(card);
    const { showToast } = useToast();

    const [touched, setTouched] = useState<Record<string, boolean>>({});

    const markTouched = useCallback((key: string) => {
        setTouched((prev) => ({ ...prev, [key]: true }));
    }, []);

    const mutation = useMutation({
        mutationFn: (body: EditPatientBody) => editPatient(patientId, body),
        onSuccess: async (data) => {
            await refetch();
            showToast('Patient updated.', 'success');
            if (!data.dmSync.ok) {
                // The DB write succeeded, so still navigate back - but don't
                // let a Dental Monitoring sync failure pass silently.
                showToast(
                    `Dental Monitoring sync failed: ${data.dmSync.error ?? 'unknown error'}`,
                    'error'
                );
            }
            onBack();
        },
        onError: (error) => {
            showToast(
                error instanceof Error ? error.message : 'Failed to update patient.',
                'error'
            );
        }
    });

    const formErrors = validateForm(values, ALL_FIELDS);
    const hasFormErrors = Object.keys(formErrors).length > 0;
    const diff = buildDiff(values, original, ALL_FIELDS);
    const hasChanges = Object.keys(diff).length > 0;
    const canSubmit = !hasFormErrors && hasChanges && !mutation.isPending;

    const handleSubmit = useCallback(() => {
        setTouched(Object.fromEntries(ALL_FIELDS.map((f) => [f.key, true])));
        if (hasFormErrors || !hasChanges) return;
        mutation.reset();
        mutation.mutate(diff);
    }, [hasFormErrors, hasChanges, diff, mutation]);

    return {
        cardLoading,
        values,
        touched,
        formErrors,
        markTouched,
        handleChange,
        canSubmit,
        isPending: mutation.isPending,
        handleSubmit
    };
}

interface FieldInputProps {
    field: EditPatientFieldConfig;
    value: string;
    error: boolean;
    onChange: (value: string) => void;
    onBlur: () => void;
}

/** A stored value predating a field's move to a fixed enum matches no known
 *  <option>, and a native <select> would silently render it as unset - surface
 *  it as its own synthetic option instead of losing it from view. */
function resolveSelectOptions(field: EditPatientFieldConfig, value: string) {
    const options = field.options ?? [];
    if (value === '' || options.some((o) => o.value === value)) return options;
    return [{ value, label: `${value} (current value, not in list)` }, ...options];
}

/** Stored phone -> react-phone-input-2 display digits: strip '+', and prepend
 *  the US/CA country code when a bare 10-digit national number was stored. */
function toPhoneInputValue(stored: string): string {
    const digits = stored.replace(/\D/g, '');
    return digits.length === 10 ? `1${digits}` : digits;
}

/** Library digits -> stored E.164-ish value. Just the dial code (nothing
 *  typed) means the field was cleared, which the endpoint stores as null. */
function fromPhoneInputValue(raw: string): string {
    const digits = raw.replace(/\D/g, '');
    if (!digits || digits === '1') return '';
    return `+${digits}`;
}

interface EditPhoneInputProps {
    value: string;
    error: boolean;
    placeholder?: string;
    onChange: (value: string) => void;
    onBlur: () => void;
}

/** Same widget and config as the case-submission form
 *  (dashboard/src/pages/patient/CreationProcess/patientDetails.tsx). Owns its
 *  display state: feeding the normalized value straight back into the widget
 *  resets it on every keystroke (focusing fires onChange with the bare dial
 *  code, which normalizes to '' - a clear). */
function EditPhoneInput({ value, error, placeholder, onChange, onBlur }: EditPhoneInputProps) {
    const [display, setDisplay] = useState(() => toPhoneInputValue(value));
    const lastEmitted = useRef(value);

    useEffect(() => {
        // Adopt external updates (the one-time card prefill), never our own echoes.
        if (value !== lastEmitted.current) {
            lastEmitted.current = value;
            setDisplay(toPhoneInputValue(value));
        }
    }, [value]);

    const handleChange = (raw: string) => {
        setDisplay(raw);
        const normalized = fromPhoneInputValue(raw);
        lastEmitted.current = normalized;
        onChange(normalized);
    };

    return (
        <PhoneInput
            country={'us'}
            onlyCountries={['us', 'ca']}
            disableDropdown
            countryCodeEditable={false}
            value={display}
            onChange={handleChange}
            onBlur={onBlur}
            inputStyle={{ width: '100%', height: '40px' }}
            inputClass={`${error ? 'border-red-500 focus:ring-red-500' : 'border-gray-300'} px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
            placeholder={placeholder}
        />
    );
}

function FieldInput({ field, value, error, onChange, onBlur }: FieldInputProps) {
    if (field.inputType === 'phone') {
        return (
            <EditPhoneInput
                value={value}
                error={error}
                placeholder={field.placeholder}
                onChange={onChange}
                onBlur={onBlur}
            />
        );
    }
    if (field.inputType === 'date') {
        return <DmDateInput value={value} onChange={onChange} error={error} />;
    }
    if (field.inputType === 'select') {
        return (
            <DmSelect
                value={value}
                onChange={onChange}
                options={resolveSelectOptions(field, value)}
                error={error}
            />
        );
    }
    if (field.inputType === 'textarea') {
        return (
            <DmTextarea
                value={value}
                onChange={onChange}
                placeholder={field.placeholder}
                error={error}
            />
        );
    }
    return (
        <input
            type={
                field.key === 'email' ? 'email' : field.inputType === 'number' ? 'number' : 'text'
            }
            className={error ? dmInputErrorClasses : dmInputClasses}
            placeholder={field.placeholder}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onBlur={onBlur}
        />
    );
}

interface PatientEditFormProps {
    values: FormValues;
    touched: Record<string, boolean>;
    formErrors: FormErrors;
    onChange: (key: string, value: string) => void;
    onBlur: (key: string) => void;
}

function PatientEditForm({ values, touched, formErrors, onChange, onBlur }: PatientEditFormProps) {
    return (
        <>
            {EDIT_PATIENT_GROUPS.map((group) => (
                <div key={group} className="bg-white rounded-xl border border-gray-200 p-6">
                    <h3 className="text-sm font-bold text-gray-800 mb-4">{group}</h3>
                    <div className="grid grid-cols-2 gap-3">
                        {fieldsInGroup(group).map((field) => {
                            const hasError = Boolean(touched[field.key] && formErrors[field.key]);
                            return (
                                <DmField
                                    key={field.key}
                                    label={field.label}
                                    required={field.required}
                                    error={hasError ? formErrors[field.key] : undefined}
                                    hint={field.hint}
                                    className={
                                        field.inputType === 'textarea' || field.fullWidth
                                            ? 'col-span-2'
                                            : undefined
                                    }
                                >
                                    <FieldInput
                                        field={field}
                                        value={values[field.key] ?? ''}
                                        error={hasError}
                                        onChange={(v) => onChange(field.key, v)}
                                        onBlur={() => onBlur(field.key)}
                                    />
                                </DmField>
                            );
                        })}
                    </div>
                </div>
            ))}
        </>
    );
}

function EditPatientLanding({ patientId, onBack }: ActionRunnerProps) {
    const {
        cardLoading,
        values,
        touched,
        formErrors,
        markTouched,
        handleChange,
        canSubmit,
        isPending,
        handleSubmit
    } = useEditPatientForm(patientId, onBack);

    return (
        <DmShellLayout title="Edit patient" onBack={onBack}>
            <div className="w-full max-w-2xl mx-auto flex flex-col gap-4">
                {cardLoading && <SkeletonCard />}

                {!cardLoading && (
                    <>
                        <PatientEditForm
                            values={values}
                            touched={touched}
                            formErrors={formErrors}
                            onChange={handleChange}
                            onBlur={markTouched}
                        />

                        <div className="flex justify-end gap-2 pt-2">
                            <DmBtnSecondary onClick={onBack} disabled={isPending}>
                                Cancel
                            </DmBtnSecondary>
                            <DmBtnPrimary onClick={handleSubmit} disabled={!canSubmit}>
                                {isPending ? 'Saving…' : 'Save'}
                            </DmBtnPrimary>
                        </div>
                    </>
                )}
            </div>
        </DmShellLayout>
    );
}

export { EditPatientLanding };
